"use client";

import React, { useState } from "react";
import Image from "next/image";
import { motion } from "motion/react";
import { Footer } from "@/components/layout/footer";
 
import {
  Mail, // FaEnvelope
  Phone, // FaPhoneAlt
  Linkedin, // FaLinkedin
  Facebook, // FaFacebook
  Instagram, // FaInstagram
  Twitter, // FaTwitter
  Github, // FaGithub
  User, // BsFillPersonFill
  Briefcase, // BsBriefcase
  MessageCircle, // BsChatDots
} from "lucide-react";


import { MainNavigation } from "@/components/layout/navbar";

// High-quality, license-free Unsplash image for restaurant contact vibe
const CONTACT_IMAGE =
  "https://images.unsplash.com/photo-1517248135467-4c7edcad3b43?auto=format&fit=crop&w=900&q=80";

// Theme for Inde Menu
const THEME = {
  colors: {
    primary: "#1D3557",
    accent: "#FFB703",
    button: "#E63946",
    buttonHover: "#FFB703",
    bgGradient: "from-yellow-50 via-white to-orange-50",
  },
  fonts: {
    display: "Playfair Display, serif",
    body: "Inter, Arial, sans-serif",
  },
};

const socialMediaLinks = [
  {
    icon: <Facebook className="h-6 w-6 text-blue-600" />,
    url: "https://www.facebook.com/",
    label: "Facebook",
  },
  {
    icon: <Instagram className="h-6 w-6 text-pink-600" />,
    url: "https://www.instagram.com/",
    label: "Instagram",
  },
  {
    icon: <MessageCircle className="h-6 w-6 text-green-600" />,
    url: "https://wa.me/265991673436",
    label: "WhatsApp",
  },
  {
    icon: <Twitter className="h-6 w-6 text-blue-400" />,
    url: "https://www.twitter.com/",
    label: "Twitter",
  },
  {
    icon: <Github className="h-6 w-6 text-gray-700" />,
    url: "https://www.github.com/",
    label: "GitHub",
  },
  {
    icon: <Linkedin className="h-6 w-6 text-blue-700" />,
    url: "https://www.linkedin.com/",
    label: "LinkedIn",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const InputField = ({
  label,
  type,
  name,
  value,
  onChange,
  error,
  icon,
}: any) => (
  <div className="mb-4">
    <label
      htmlFor={name}
      className="block text-gray-700 font-semibold mb-2 flex items-center"
    >
      {icon && <span className="mr-2">{icon}</span>}
      {label}
    </label>
    <input
      type={type}
      id={name}
      name={name}
      value={value}
      onChange={onChange}
      placeholder={label}
      autoComplete={name}
      className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
    />
    {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
  </div>
);

const ContactUsPage: React.FC = () => {
  const [formValues, setFormValues] = useState({
    name: "",
    email: "",
    jobTitle: "",
    message: "",
  });
  const [errors, setErrors] = useState({
    name: "",
    email: "",
    jobTitle: "",
    message: "",
  });
  const [submitStatus, setSubmitStatus] = useState("");
  const [loading, setLoading] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const validate = (): boolean => {
    const newErrors = { name: "", email: "", jobTitle: "", message: "" };
    if (!formValues.name) newErrors.name = "Name is required";
    if (!formValues.email) newErrors.email = "Email is required";
    if (!formValues.jobTitle) newErrors.jobTitle = "Job title is required";
    if (!formValues.message) newErrors.message = "Message is required";
    setErrors(newErrors);
    return !Object.values(newErrors).some((error) => error !== "");
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const response = await fetch("/api/inquiries", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formValues),
      });

      if (!response.ok) throw new Error("Failed to submit inquiry");

      setSubmitStatus("Your inquiry has been submitted successfully!");
      setFormValues({ name: "", email: "", jobTitle: "", message: "" });
    } catch (error) {
      setSubmitStatus("There was an error submitting your inquiry.");
    }
    setLoading(false);
  };

  return (
    <main
      className={`font-sans bg-gradient-to-b ${THEME.colors.bgGradient} min-h-screen flex flex-col justify-between`}
      style={{ fontFamily: THEME.fonts.body }}
    >
      <MainNavigation />

      {/* Hero Header */}
      <section className="w-full px-3 py-8 flex flex-col items-center bg-white/50">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          className="max-w-2xl mx-auto text-center"
        >
          <h1
            className="text-3xl md:text-4xl font-extrabold mb-3"
            style={{
              fontFamily: THEME.fonts.display,
              color: THEME.colors.primary,
            }}
          >
            Contact Inde Menu
          </h1>
          <p className="text-lg text-gray-700 mb-2">
            Hungry for answers? We're here to help with your dining, booking,
            and food service inquiries.
          </p>
        </motion.div>
      </section>

      {/* Main Contact Grid */}
      <section className="w-full max-w-7xl mx-auto px-2 md:px-6 py-2 md:py-12 flex-1">
        <div className="flex flex-col md:flex-row gap-10 items-stretch justify-center">
          {/* Left: Contact Form */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="w-full md:w-1/2 bg-white rounded-2xl shadow-lg p-6 md:p-10 flex flex-col"
          >
            <h2
              className="text-2xl md:text-3xl font-bold mb-6 text-center"
              style={{ color: THEME.colors.primary }}
            >
              Get in Touch
            </h2>
            <form
              onSubmit={handleSubmit}
              className="flex flex-col gap-2"
              autoComplete="off"
            >
              <InputField
                label="Your Name"
                type="text"
                name="name"
                value={formValues.name}
                onChange={handleChange}
                error={errors.name}
                icon={<User className="h-5 w-5 text-gray-500" />}
              />
              <InputField
                label="Your Email"
                type="email"
                name="email"
                value={formValues.email}
                onChange={handleChange}
                error={errors.email}
                icon={<Mail className="h-5 w-5 text-gray-500" />}
              />
              <InputField
                label="Job Title"
                type="text"
                name="jobTitle"
                value={formValues.jobTitle}
                onChange={handleChange}
                error={errors.jobTitle}
                icon={<Briefcase className="h-5 w-5 text-gray-500" />}
              />
              <div className="mb-4">
                <label
                  htmlFor="message"
                  className="block text-gray-700 font-semibold mb-2 flex items-center"
                >
                  <MessageCircle className="mr-2 h-5 w-5 text-gray-500" />
                  Your Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formValues.message}
                  onChange={handleChange}
                  placeholder="Your Message"
                  rows={4}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
                {errors.message && (
                  <p className="text-red-500 text-sm mt-1">{errors.message}</p>
                )}
              </div>
              <button
                type="submit"
                disabled={loading}
                className="bg-orange-600 hover:bg-orange-700 text-white py-3 px-6 rounded-full shadow-md transition duration-200 font-semibold focus:outline-none focus:ring-2 focus:ring-orange-300"
              >
                {loading ? "Submitting..." : "Submit"}
              </button>
              {submitStatus && (
                <p className="mt-4 text-center text-green-700">
                  {submitStatus}
                </p>
              )}
            </form>
          </motion.div>

          {/* Right: Contact Info & Social */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="w-full md:w-1/2 bg-white rounded-2xl shadow-lg p-6 md:p-10 flex flex-col items-center"
          >
            <Image
              src={CONTACT_IMAGE}
              alt="Contact Inde Menu"
              width={500}
              height={300}
              className="rounded-lg object-cover mb-6"
              priority
            />
            <h2
              className="text-2xl md:text-3xl font-bold mb-6 text-center"
              style={{ color: THEME.colors.primary }}
            >
              Contact Information
            </h2>
            <div className="space-y-4 mb-6 w-full max-w-sm mx-auto">
              <p className="flex items-center text-gray-700 justify-center">
                <Phone className="mr-2 h-5 w-5 text-gray-500" />
                <span>
                  <strong>Phone:</strong> +265 (0) 9916 73 436
                </span>
              </p>
              <p className="flex items-center text-gray-700 justify-center">
                <Mail className="mr-2 h-5 w-5 text-gray-500" />
                <span>
                  <strong>Email:</strong> inde.menu@gmail.com
                </span>
              </p>
              <p className="flex items-center text-gray-700 justify-center">
                <MessageCircle className="mr-2 h-5 w-5 text-green-500" />
                <span>
                  <strong>WhatsApp:</strong> +265 9916 73 436
                </span>
              </p>
            </div>
            <div className="flex justify-center space-x-4 mt-4">
              {socialMediaLinks.map((link, index) => (
                <a
                  key={index}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={link.label}
                  className="text-gray-500 hover:text-orange-600 transition-colors duration-300"
                >
                  {link.icon}
                </a>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </main>
  );
};

export default ContactUsPage;
